import { OtsDateTime } from '../../components/OtsDateTime';
import { useState } from 'react';
import type { RrcEp9WasteProfile } from '@ots-frontend/ots-data';
import {
  OtsDataTable,
  OtsDataTableHead,
  OtsDataTableBody,
  OtsDataTableRow,
  OtsDataTableHeaderCell,
  OtsDataTableCell,
  OtsDataTableSortableHeaderCell,
} from '../../components/OtsDataTable';
import {
  OtsDataTableLink,
  OtsDataTableEmpty,
} from '../../components/OtsDataTableCells';
import { OtsTablePanel } from '../../components/OtsTablePanel';
import { OtsSearchInput } from '../../components/OtsSearchInput';
import { OtsActionButton } from '../../components/OtsButton';
import { OtsSwitch } from '../../components/OtsSwitch';
import { OtsInlineAlert } from '../../components/OtsInlineAlert';
import { ComplianceStatusBadge } from '../ComplianceStatusBadge';
import { ep9WasteCodeLabel } from './rrc-ep9-waste-profile-ui';

export type RrcEp9WasteProfilesViewProps = {
  profiles: RrcEp9WasteProfile[];
  loading?: boolean;
  error?: string;
  compact?: boolean;
  fill?: boolean;
  includeHistory?: boolean;
  onIncludeHistory?: (value: boolean) => void;
  onOpen: (profile: RrcEp9WasteProfile) => void;
  onNew?: () => void;
  onRefresh?: () => void;
};
export const RrcEp9WasteProfilesView = ({
  profiles,
  loading,
  error,
  compact,
  fill = false,
  includeHistory,
  onIncludeHistory,
  onOpen,
  onNew,
  onRefresh,
}: RrcEp9WasteProfilesViewProps) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [ascending, setAscending] = useState(true);
  const filtered = profiles
    .filter((profile) =>
      [
        profile.wasteId,
        profile.objectName,
        profile.generatorOrgName,
        ep9WasteCodeLabel(profile.wasteType),
        profile.objectType,
        profile.status,
      ].some((value) =>
        value?.toLowerCase().includes(search.toLowerCase().trim())
      )
    )
    .sort(
      (a, b) =>
        (ascending ? 1 : -1) *
          (a.wasteId ?? '').localeCompare(b.wasteId ?? '') ||
        b.version - a.version
    );
  const pageSize = 15;
  const currentPage = Math.min(
    page,
    Math.max(1, Math.ceil(filtered.length / pageSize))
  );
  return (
    <OtsTablePanel
      title="RRC EP-9 Waste Profiles"
      compact={compact}
      fill={fill}
      description={`${filtered.length} profiles`}
      actions={
        <>
          {onRefresh && (
            <OtsActionButton
              size="compact"
              onClick={onRefresh}
              disabled={loading}
            >
              Refresh
            </OtsActionButton>
          )}
          {onNew && (
            <OtsActionButton variant="primary" size="compact" onClick={onNew}>
              New EP-9 waste profile
            </OtsActionButton>
          )}
        </>
      }
      toolbar={
        <>
          <OtsSearchInput
            label="Search EP-9 waste profiles"
            value={search}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
              setPage(1);
            }}
            onClear={() => {
              setSearch('');
              setPage(1);
            }}
            size={compact ? 'compact' : 'default'}
          />
          {onIncludeHistory && (
            <OtsSwitch
              label="Include previous versions"
              checked={includeHistory ?? false}
              onCheckedChange={(value) => {
                setPage(1);
                onIncludeHistory(value);
              }}
              size={compact ? 'compact' : 'default'}
            />
          )}
        </>
      }
      pagination={{
        page: currentPage,
        pageSize,
        totalItems: filtered.length,
        onPageChange: setPage,
        itemLabel: 'profiles',
        disabled: loading,
      }}
    >
      {loading ? (
        <OtsDataTableEmpty loading loadingText="Loading EP-9 waste profiles…">
          {null}
        </OtsDataTableEmpty>
      ) : error ? (
        <div className="p-4">
          <OtsInlineAlert
            tone="danger"
            title="EP-9 waste profiles unavailable"
            action={
              onRefresh && (
                <OtsActionButton size="compact" onClick={onRefresh}>
                  Try again
                </OtsActionButton>
              )
            }
          >
            {error}
          </OtsInlineAlert>
        </div>
      ) : !filtered.length ? (
        <OtsDataTableEmpty compact={compact}>
          {search
            ? 'No EP-9 waste profiles match your search.'
            : 'No EP-9 waste profiles have been configured.'}
        </OtsDataTableEmpty>
      ) : (
        <OtsDataTable
          aria-label="RRC EP-9 waste profiles"
          density={compact ? 'compact' : 'default'}
          contained={false}
        >
          <OtsDataTableHead>
            <OtsDataTableRow>
              <OtsDataTableSortableHeaderCell
                direction={ascending ? 'asc' : 'desc'}
                onSort={() => setAscending(!ascending)}
              >
                Waste ID
              </OtsDataTableSortableHeaderCell>
              {[
                'Type',
                'Waste type',
                'Effective start',
                'Effective end',
                'Status',
                'Version',
              ].map((label) => (
                <OtsDataTableHeaderCell key={label}>
                  {label}
                </OtsDataTableHeaderCell>
              ))}
            </OtsDataTableRow>
          </OtsDataTableHead>
          <OtsDataTableBody>
            {filtered
              .slice((currentPage - 1) * pageSize, currentPage * pageSize)
              .map((profile) => (
                <OtsDataTableRow key={profile.id}>
                  <OtsDataTableCell>
                    <OtsDataTableLink
                      onClick={() => onOpen(profile)}
                      ariaLabel={`Open ${
                        profile.wasteId ??
                        profile.objectName ??
                        'EP-9 waste profile'
                      }, version ${profile.version}`}
                    >
                      {profile.wasteId ??
                        profile.objectName ??
                        'Unassigned waste ID'}
                    </OtsDataTableLink>
                  </OtsDataTableCell>
                  <OtsDataTableCell>{profile.objectType}</OtsDataTableCell>
                  <OtsDataTableCell>
                    {ep9WasteCodeLabel(profile.wasteType)}
                  </OtsDataTableCell>
                  <OtsDataTableCell>
                    <OtsDateTime
                      value={profile.effectiveStartDttm}
                      empty="Not set"
                    />
                  </OtsDataTableCell>
                  <OtsDataTableCell>
                    <OtsDateTime
                      value={profile.effectiveEndDttm}
                      empty="No end date"
                    />
                  </OtsDataTableCell>
                  <OtsDataTableCell>
                    <ComplianceStatusBadge
                      status={profile.status}
                      compact={compact}
                    />
                  </OtsDataTableCell>
                  <OtsDataTableCell>{profile.version}</OtsDataTableCell>
                </OtsDataTableRow>
              ))}
          </OtsDataTableBody>
        </OtsDataTable>
      )}
    </OtsTablePanel>
  );
};
